import { useState, useEffect, useCallback, useRef } from "react";
import { saveDraft as saveDraftAPI, getDraft as getDraftAPI, deleteDraft as deleteDraftAPI } from "../../../services/draftService";
import { useChat } from "../../../context/ChatContext";

/**
 * Custom hook for managing draft messages with auto-save and persistence
 * @param {string} chatId - The chat/channel ID
 * @param {string} chatType - "channel" or "direct"
 * @param {string} initialValue - Initial draft content (optional)
 * @returns {Object} Draft management utilities
 */
export function useDraft(chatId, chatType, initialValue = "") {
  const [value, setValue] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null);

  const { refetchDrafts } = useChat();

  const debounceRef = useRef(null);
  const isInitialLoad = useRef(true);
  const prevChatIdRef = useRef(chatId);
  const currentConversationRef = useRef({ chatId, chatType });
  const suppressAutoSaveUntilRef = useRef(0); // timestamp

  // High-frequency refs for cleanup access
  const latestValueRef = useRef(value);
  const latestChatIdRef = useRef(chatId);
  const latestChatTypeRef = useRef(chatType);
  const latestScheduledAtRef = useRef(scheduledAt);
  // Stable ref to refetchDrafts — lets cleanup/save always call the latest
  // version without needing it in any effect's dependency array.
  const refetchDraftsRef = useRef(refetchDrafts);
  useEffect(() => { refetchDraftsRef.current = refetchDrafts; }, [refetchDrafts]);

  useEffect(() => {
    latestValueRef.current = value;
    latestChatIdRef.current = chatId;
    latestChatTypeRef.current = chatType;
    latestScheduledAtRef.current = scheduledAt;
  }, [value, chatId, chatType, scheduledAt]);

  // Storage key for localStorage
  const getStorageKey = useCallback(() => {
    const normalizedChatType = chatType === 'dm' ? 'direct' : chatType;
    return `draft_${normalizedChatType}_${chatId}`;
  }, [chatId, chatType]);

  // Suppress auto-save for a short time window (200ms)
  // Used when we need to update state without triggering auto-save (e.g., after scheduling)
  const suppressAutoSave = useCallback(() => {
    suppressAutoSaveUntilRef.current = Date.now() + 200; // suppress for 200ms
  }, []);

  // Main effect: handle chat changes, load drafts, manage state
  useEffect(() => {
    let isMounted = true;

    // Detect chat change by comparing with stored previous ID
    const previousChatId = prevChatIdRef.current;
    const chatHasChanged = previousChatId !== chatId;

    // If chat changed, immediately clear state and prepare for new draft
    if (chatHasChanged) {
      console.log('[DraftDebug] Chat changed from', previousChatId, 'to', chatId, '- clearing draft state');
      setValue("");
      setScheduledAt(null);
      setLastSaved(null);
      setHasUnsavedChanges(false);
      isInitialLoad.current = true;
      prevChatIdRef.current = chatId;

      // Cancel any pending debounced saves from previous chat
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    }

    // Update conversation tracking ref
    if (chatId && chatType) {
      currentConversationRef.current = { chatId, chatType };
    }

    // Don't attempt to load if we don't have a chatId yet
    if (!chatId || !chatType) {
      setIsLoading(false);
      return;
    }

    const loadDraft = async () => {
      if (!chatId) {
        console.log("[useDraft] ⚠️ loadDraft skipped: no chatId");
        return;
      }

      setIsLoading(true);
      console.log("[useDraft] 📥 Loading draft for chatId:", chatId);
      try {
        let draftContent = "";
        let draftScheduledAt = null;
        let draftUpdatedAt = null;

        // Try localStorage first (offline support)
        // Compute key inline — avoids needing getStorageKey in effect deps
        const normalizedType = chatType === 'dm' ? 'direct' : chatType;
        const storageKey = `draft_${normalizedType}_${chatId}`;
        const localDraft = localStorage.getItem(storageKey);

        if (localDraft) {
          try {
            const parsed = JSON.parse(localDraft);
            draftContent = parsed.content || "";
            draftScheduledAt = parsed.scheduledAt || null;
            draftUpdatedAt = parsed.updatedAt ? new Date(parsed.updatedAt) : null;
            console.log("[useDraft] ✅ Loaded from localStorage:", {
              contentLength: draftContent.length,
              scheduledAt: draftScheduledAt,
            });
          } catch (e) {
            console.warn("[useDraft] ❌ Failed to parse local draft:", e);
          }
        } else {
          console.log("[useDraft] 📭 No localStorage draft found");
        }

        // Fetch from backend (may overwrite localStorage data)
        try {
          console.log("[useDraft] 🌐 Fetching draft from backend API...");
          const response = await getDraftAPI(chatId);
          if (response?.success && response?.data) {
            const backendDraft = response.data;
            console.log("[useDraft] ✅ Backend draft received:", {
              _id: backendDraft._id,
              contentLength: backendDraft.content?.length,
              scheduledAt: backendDraft.scheduledAt,
            });
            draftContent = backendDraft.content || "";
            draftScheduledAt = backendDraft.scheduledAt || null;
            draftUpdatedAt = backendDraft.updatedAt ? new Date(backendDraft.updatedAt) : null;

            // Sync localStorage with backend
            localStorage.setItem(storageKey, JSON.stringify({
              content: draftContent,
              scheduledAt: draftScheduledAt,
              updatedAt: draftUpdatedAt,
            }));
            console.log("[useDraft] 💾 Synced to localStorage");
          } else {
            console.log("[useDraft] ⚠️ No backend draft data:", response);
          }
        } catch (error) {
          console.warn("[useDraft] 🌐 Backend fetch failed, using localStorage:", error.message);
        }

        // Only update state if component is still mounted
        if (isMounted) {
          console.log("[useDraft] 📝 Updating React state:", {
            contentLength: draftContent.length,
            hasScheduled: !!draftScheduledAt,
          });
          setValue(draftContent);
          setScheduledAt(draftScheduledAt);
          setLastSaved(draftUpdatedAt);
          isInitialLoad.current = false;
        }
      } catch (error) {
        console.error("[useDraft] ❌ Error loading draft:", error);
        if (isMounted) {
          setValue("");
          setScheduledAt(null);
          setLastSaved(null);
          isInitialLoad.current = false;
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDraft();

    // ── INDUSTRY STANDARD: Save draft only on chat switch/unmount ────────────────
    return () => {
      isMounted = false;

      const valToSave = latestValueRef.current;
      const idToSave = latestChatIdRef.current;
      const typeToSave = latestChatTypeRef.current;
      const scheduledToSave = latestScheduledAtRef.current;

      // Only save if there's actual content and it's not the initial load state
      if (idToSave && typeToSave && valToSave?.trim() && !isInitialLoad.current) {
        console.log(`[useDraft] 💾 Saving draft for ${idToSave} on navigation/unmount`);

        const normalizedChatType = typeToSave === 'dm' ? 'direct' : typeToSave;
        const payload = {
          chatId: idToSave,
          chatType: normalizedChatType,
          content: valToSave.trim(),
        };
        if (scheduledToSave) payload.scheduledAt = scheduledToSave;

        // Fire-and-forget save (no await to avoid blocking unmount)
        saveDraftAPI(payload).then(response => {
          if (response?.success) {
            const storageKey = `draft_${normalizedChatType}_${idToSave}`;
            localStorage.setItem(storageKey, JSON.stringify({
              content: valToSave.trim(),
              scheduledAt: scheduledToSave,
              updatedAt: new Date()
            }));
            refetchDraftsRef.current().catch(() => { });
          }
        }).catch(err => {
          console.error(`[useDraft] ❌ Failed to save draft for ${idToSave}:`, err.message);
        });
      }
    };
  }, [chatId, chatType]); // ← stable strings only; getStorageKey/refetchDrafts accessed via refs

  // ── REMOVED: Auto-save debounced effect (Requirement: Save only on switch) ──
  // Auto-save timer logic was here.

  // Manual immediate save
  const saveDraft = useCallback(async (contentToSave = value) => {
    console.log("[useDraft] 🔄 Manual saveDraft called:", {
      chatId,
      chatType,
      contentLength: contentToSave?.length,
      scheduledAt,
    });
    if (!chatId || !chatType) {
      console.warn("[useDraft] ❌ Cannot save - missing chatId or chatType");
      return;
    }

    const currentChatId = chatId;
    const currentChatType = chatType;
    const currentStorageKey = getStorageKey();
    const trimmedContent = contentToSave.trim();

    try {
      if (!trimmedContent) {
        console.log("[useDraft] 🗑️ Content empty, deleting draft for chatId:", currentChatId);
        await deleteDraftAPI(currentChatId);
        localStorage.removeItem(currentStorageKey);
        setValue("");
        setLastSaved(null);
        setHasUnsavedChanges(false);
        setScheduledAt(null);
        await refetchDraftsRef.current().catch(e => console.error("refetchDrafts failed:", e));
        return;
      }

      const normalizedChatType = currentChatType === 'dm' ? 'direct' : currentChatType;
      const payload = {
        chatId: currentChatId,
        chatType: normalizedChatType,
        content: trimmedContent,
      };

      if (scheduledAt) {
        payload.scheduledAt = scheduledAt;
      }

      console.log("[useDraft] 📤 Sending to API:", payload);
      const response = await saveDraftAPI(payload);

      if (response?.success) {
        const savedDraft = response.data;
        console.log("[useDraft] ✅ Manual save API success:", {
          _id: savedDraft._id,
          chatId: savedDraft.chatId,
          scheduledAt: savedDraft.scheduledAt,
        });
        const storageData = {
          content: trimmedContent,
          updatedAt: new Date(),
        };

        if (scheduledAt) {
          storageData.scheduledAt = scheduledAt;
        }

        localStorage.setItem(currentStorageKey, JSON.stringify(storageData));
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        await refetchDraftsRef.current().catch(e => console.error("refetchDrafts failed:", e));
        console.log("[useDraft] ✅ Manual save completed");
      }
    } catch (error) {
      console.error("[useDraft] ❌ Manual save failed:", error.message);
      throw error;
    }
  }, [value, chatId, chatType, scheduledAt, getStorageKey]);

  // Clear draft
  const clearDraft = useCallback(async () => {
    if (!chatId) return;

    const currentChatId = chatId;
    const currentStorageKey = getStorageKey();

    try {
      // Clear local state immediately for instant clearing UX
      setValue("");
      setLastSaved(null);
      setHasUnsavedChanges(false);
      setScheduledAt(null);

      // Perform backend deletion in background
      await deleteDraftAPI(currentChatId);
      localStorage.removeItem(currentStorageKey);
      await refetchDraftsRef.current().catch(e => console.error("refetchDrafts failed:", e));
    } catch (error) {
      console.error("[useDraft] Error clearing draft:", error);
      throw error;
    }
  }, [chatId, getStorageKey]);

  // Set value externally
  const handleSetValue = useCallback((newValue) => {
    setValue(newValue);
    setHasUnsavedChanges(true);
  }, []);

  // Scheduled message handling
  const setScheduled = useCallback((dateTimeString) => {
    setScheduledAt(dateTimeString);
    setHasUnsavedChanges(true);
  }, []);

  const clearScheduled = useCallback(() => {
    setScheduledAt(null);
    setHasUnsavedChanges(true);
  }, []);

  return {
    // State
    value,
    isLoading,
    lastSaved,
    hasUnsavedChanges,
    scheduledAt,
    // Actions
    setValue: handleSetValue,
    saveDraft,
    clearDraft,
    setScheduled,
    clearScheduled,
    suppressAutoSave,
    // Utility
    storageKey: getStorageKey(),
  };
}
